/**
 * Récupération côté client des SÉRIES TEMPORELLES de stats (graphe d'évolution
 * au survol). Le serveur échantillonne chaque match en direct et expose la série
 * sur /data/stats-history/<id>.json. Cache mémoire avec TTL court pour un match
 * en cours (la série grandit), permanent sinon ; un échec réseau renvoie le
 * dernier état connu ou une série vide (règle 8 — jamais d'écran cassé).
 */
import type { LiveStats } from "./liveSchema.ts";

export interface StatPoint {
  readonly m: number | null; // minute de match (null si inconnue)
  readonly s: LiveStats; // stats [domicile, extérieur] à cet instant
}
export interface StatSeries {
  readonly id: number;
  readonly points: readonly StatPoint[];
}

const TTL_MS = 20_000; // re-fetch d'un match en cours (au-delà, la série a bougé)
const cache = new Map<number, { at: number; series: StatSeries }>();

/** Série temporelle d'un match. `live` force le rafraîchissement passé le TTL. */
export async function fetchStatHistory(matchId: number, opts: { live?: boolean } = {}): Promise<StatSeries> {
  const hit = cache.get(matchId);
  if (hit && (!opts.live || Date.now() - hit.at < TTL_MS)) return hit.series;
  try {
    const res = await fetch(`/data/stats-history/${matchId}.json`, { cache: "no-store" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const raw = (await res.json()) as Partial<StatSeries>;
    const series: StatSeries = { id: matchId, points: Array.isArray(raw.points) ? raw.points : [] };
    cache.set(matchId, { at: Date.now(), series });
    return series;
  } catch {
    return hit?.series ?? { id: matchId, points: [] };
  }
}

/** Extrait la série d'UNE stat : points [minute, domicile, extérieur] exploitables
 *  (minute connue et au moins une valeur non nulle). */
export function pluckSeries(
  series: StatSeries,
  key: string,
): ReadonlyArray<readonly [number, number | null, number | null]> {
  const out: Array<[number, number | null, number | null]> = [];
  for (const p of series.points) {
    if (p.m == null) continue;
    const pair = p.s[key];
    if (!pair) continue;
    if (pair[0] == null && pair[1] == null) continue;
    out.push([p.m, pair[0], pair[1]]);
  }
  return out;
}
